import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { Badge, Box, ButtonBase, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Switch, Typography } from '@mui/material';
import React from 'react';
import { globalMenuWidthSmall } from '../../GlobalMenu/constants';
import { JuvareLogo } from '../../JuvareLogo';
import { setPreferredThemeMode, themeColors, themeMode } from '../../JuvareTheme';
import { themeColorsDark } from '../../JuvareTheme/colors/themeColorsDark';
import { useCommonWebTranslation, useSmallDevice, useSmallOrMediumDevice } from '../../hooks';
import { ProfileScopeMenuItem } from './ProfileScopeMenuItem';
/**
 * Renders a profile button with a dropdown menu displaying user details, profile scopes,
 * access to other Juvare solutions, a theme switcher, and logout functionality.
 * Designed for use in the Global App header.
 *
 * @param {ProfileButtonProps} props - Props for configuring the profile button behavior and content.
 * @param {React.ReactNode} [props.profileAvatarSmall] - Small version of the user avatar shown in the header.
 * @param {React.ReactNode} [props.profileAvatarLarge] - Large avatar shown in the dropdown menu.
 * @param {React.ReactNode} [props.profileName] - User's name shown in the dropdown.
 * @param {React.ReactNode} [props.profileBadge] - Optional badge shown next to the profile name.
 * @param {React.ReactNode} [props.profileRole] - Role or title shown in the dropdown.
 * @param {() => void} [props.profileOnClick] - Callback when the profile menu item is clicked.
 * @param {string} [props.juvareHref] - URL to other Juvare solutions.
 * @param {() => void} [props.juvareOnClick] - Callback instead of `juvareHref` when the Juvare option is clicked.
 * @param {() => void} [props.logOutOnClick] - Callback triggered on logout.
 * @param {string} [props.profilePrimaryScopeLabel] - Label for the primary scope selector.
 * @param {string} [props.profilePrimaryScopeText] - Display text for the primary scope.
 * @param {string | number} [props.profilePrimaryScopeValue] - Selected value for the primary scope.
 * @param {() => Promise<ProfileScopeOption[]>} [props.profilePrimaryScopeOptions] - Async function returning options for the primary scope selector.
 * @param {(newScope: ProfileScopeOption) => void} [props.onProfilePrimaryScopeChange] - Callback triggered on primary scope change.
 * @param {string} [props.profilePrimaryScopeAriaLabel] - ARIA label for the primary scope selector.
 * @param {string} [props.profileSecondaryScopeLabel] - Label for the secondary scope selector.
 * @param {string} [props.profileSecondaryScopeText] - Display text for the secondary scope.
 * @param {string | number} [props.profileSecondaryScopeValue] - Selected value for the secondary scope.
 * @param {() => Promise<ProfileScopeOption[]>} [props.profileSecondaryScopeOptions] - Async function returning options for the secondary scope selector.
 * @param {(newScope: ProfileScopeOption) => void} [props.onProfileSecondaryScopeChange] - Callback triggered on secondary scope change.
 * @param {string} [props.profileSecondaryScopeAriaLabel] - ARIA label for the secondary scope selector.
 * @param {boolean} [props.themeSwitcher] - Enables theme switcher toggle if true.
 *
 * @returns {JSX.Element} The rendered profile button and dropdown menu.
 */
export const ProfileButton = (props) => {
    var _a;
    const { t } = useCommonWebTranslation();
    const smallDevice = useSmallDevice();
    const smallOrMediumDevice = useSmallOrMediumDevice();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const openMenu = (event) => setAnchorEl(event.currentTarget);
    const closeMenu = () => setAnchorEl(null);
    const handleClose = () => {
        setAnchorEl(null);
    };
    const getLabelText = (props) => {
        if (!props.profilePrimaryScopeText) {
            return t('common.profile');
        }
        if (!props.profileSecondaryScopeText) {
            return props.profilePrimaryScopeText;
        }
        return `${props.profilePrimaryScopeText} ${props.profileSecondaryScopeText}`;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ButtonBase, { focusRipple: true, "aria-label": getLabelText(props), sx: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: props.profilePrimaryScopeText ? 'flex-start' : 'center',
                alignSelf: 'stretch',
                width: props.profilePrimaryScopeText ? undefined : globalMenuWidthSmall - 4,
                minWidth: globalMenuWidthSmall - 4,
                ml: '4px',
                pl: props.profilePrimaryScopeText ? 2 : undefined,
                pr: props.profilePrimaryScopeText ? 2 : undefined,
                gap: props.profilePrimaryScopeText ? 2 : undefined,
                '&:hover': {
                    // Global App header is always dark.
                    backgroundColor: themeColorsDark.overlay.hover,
                },
            }, onClick: openMenu, "data-test": 'app-page-header-profile-button' },
            props.profileBadge != null ? (React.createElement(Badge, { color: 'secondary', variant: 'dot', sx: {
                    '& .MuiBadge-dot': {
                        // Global App header is always dark.
                        backgroundColor: themeColorsDark.accent.red.subtle,
                    },
                } }, props.profileAvatarSmall)) : (props.profileAvatarSmall),
            props.profilePrimaryScopeText && (React.createElement(React.Fragment, null,
                !smallOrMediumDevice && (React.createElement(Box, null,
                    React.createElement(Typography, { variant: 'subtitle2', sx: {
                            maxWidth: 234,
                            // Global App header is always dark.
                            color: themeColorsDark.text.main,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        } }, props.profilePrimaryScopeText),
                    props.profileSecondaryScopeText && (React.createElement(Typography, { variant: 'body2', sx: {
                            maxWidth: 234,
                            // Global App header is always dark.
                            color: themeColorsDark.text.subtle,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        } }, props.profileSecondaryScopeText)))),
                open ? (React.createElement(ArrowDropUpIcon, { sx: { color: themeColorsDark.icon.subtle } })) : (React.createElement(ArrowDropDownIcon, { sx: { color: themeColorsDark.icon.subtle } }))))),
        React.createElement(Menu, { anchorEl: anchorEl, anchorOrigin: {
                horizontal: smallDevice ? globalMenuWidthSmall - 4 : props.profilePrimaryScopeText ? 'right' : globalMenuWidthSmall - 8,
                vertical: 'bottom',
            }, transformOrigin: { horizontal: 'right', vertical: 'top' }, marginThreshold: 0, open: open, onClose: handleClose, PaperProps: smallDevice
                ? {
                    style: {
                        width: '100%',
                        maxWidth: '100%',
                    },
                }
                : props.profilePrimaryScopeText
                    ? {
                        style: {
                            marginLeft: -8,
                        },
                    }
                    : undefined },
            React.createElement(MenuItem, { onClick: () => {
                    if (props.profileOnClick) {
                        props.profileOnClick();
                    }
                    closeMenu();
                } },
                React.createElement(Box, { sx: { minWidth: 320, maxWidth: 480, pt: 1, pb: 1, display: 'flex', alignItems: 'center', gap: 2 } },
                    (props.profileAvatarLarge || props.profileAvatarSmall) && (React.createElement(Box, null, (_a = props.profileAvatarLarge) !== null && _a !== void 0 ? _a : props.profileAvatarSmall)),
                    React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' } },
                        props.profileName && (React.createElement(Box, { sx: { display: 'flex', alignItems: 'center' } },
                            React.createElement(Typography, { variant: 'subtitle1', sx: { color: themeColors.text.main, overflow: 'hidden', textOverflow: 'ellipsis' } }, props.profileName),
                            props.profileBadge != null && (React.createElement(Typography, { variant: 'caption', sx: {
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    boxSizing: 'border-box',
                                    minWidth: '20px',
                                    height: '20px',
                                    marginLeft: '4px',
                                    padding: '0px 6px',
                                    borderRadius: '10px',
                                    lineHeight: 1,
                                    color: 'common.white',
                                    backgroundColor: themeColors.accent.red.subtle,
                                } }, props.profileBadge)))),
                        props.profileRole && (React.createElement(Typography, { variant: 'body2', sx: { color: themeColors.text.subtle, overflow: 'hidden', textOverflow: 'ellipsis' } }, props.profileRole))))),
            props.profilePrimaryScopeText && (React.createElement(ProfileScopeMenuItem, { selectedValue: props.profilePrimaryScopeValue, selectedText: props.profilePrimaryScopeText, label: props.profilePrimaryScopeLabel, options: props.profilePrimaryScopeOptions, onProfileScopeChange: props.onProfilePrimaryScopeChange, "data-test": 'primary-profile-scope-select', "aria-label": props.profilePrimaryScopeAriaLabel })),
            props.profilePrimaryScopeText && props.profileSecondaryScopeText && (React.createElement(ProfileScopeMenuItem, { selectedValue: props.profileSecondaryScopeValue, selectedText: props.profileSecondaryScopeText, label: props.profileSecondaryScopeLabel, options: props.profileSecondaryScopeOptions, onProfileScopeChange: props.onProfileSecondaryScopeChange, "data-test": 'secondary-profile-scope-select', "aria-label": props.profileSecondaryScopeAriaLabel })),
            (props.juvareHref || props.juvareOnClick || props.logOutOnClick) && React.createElement(Divider, { sx: { ml: 2, mr: 2 } }),
            (props.juvareHref || props.juvareOnClick) && (React.createElement(MenuItem, { "aria-label": t('globalHeader.otherJuvareSolutions'), onClick: () => {
                    if (props.juvareOnClick) {
                        props.juvareOnClick();
                    }
                    else if (props.juvareHref) {
                        window.location.assign(props.juvareHref);
                    }
                    closeMenu();
                } },
                React.createElement(ListItemIcon, { sx: { pt: 1, pb: 1 } },
                    React.createElement(Box, { sx: { width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                        React.createElement(Box, { sx: { display: 'flex' } },
                            React.createElement(JuvareLogo, null)))),
                React.createElement(ListItemText, null,
                    React.createElement(Typography, { sx: { color: themeColors.text.main } }, t('globalHeader.otherJuvareSolutions'))))),
            props.themeSwitcher && (React.createElement(MenuItem, { "aria-label": t('globalHeader.darkModeSwitcher'), onClick: () => {
                    setPreferredThemeMode(themeMode === 'light' ? 'dark' : 'light');
                    window.location.reload();
                } },
                React.createElement(ListItemIcon, { sx: { pt: 1, pb: 1 } },
                    React.createElement(DarkModeOutlinedIcon, { sx: { color: themeColors.icon.main } })),
                React.createElement(ListItemText, null,
                    React.createElement(Typography, { sx: { color: themeColors.text.main } }, t('globalHeader.darkMode'))),
                React.createElement(Switch, { checked: themeMode === 'dark', tabIndex: -1, sx: { pointerEvents: 'none' } }))),
            props.logOutOnClick && (React.createElement(MenuItem, { onClick: () => {
                    if (props.logOutOnClick) {
                        props.logOutOnClick();
                    }
                    closeMenu();
                } },
                React.createElement(ListItemIcon, { sx: { pt: 1, pb: 1 } },
                    React.createElement(LogoutIcon, { sx: { color: themeColors.icon.main } })),
                React.createElement(ListItemText, null,
                    React.createElement(Typography, { sx: { color: themeColors.text.main } }, t('globalHeader.logOut'))))))));
};
